import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Paper,
    Chip,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
    Collapse,
} from '@mui/material';
import {
    Timeline,
    TimelineItem,
    TimelineSeparator,
    TimelineConnector,
    TimelineContent,
    TimelineOppositeContent,
} from '@mui/lab';
import {
    History as HistoryIcon,
    // Receipt as ReceiptIcon,
    // Payment as PaymentIcon,
    // Update as UpdateIcon,
    // CheckCircle as CheckCircleIcon,
    // Cancel as CancelIcon,
    // Email as EmailIcon,
    // Print as PrintIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { InvoiceHistory } from '../../../../types';
import { request } from '../../../../../../utils/request';

interface InvoiceHistoryTimelineProps {
    invoiceId: string;
    gstType: 'GST' | 'NON-GST';
}

// const getActionIcon = (action: HistoryAction) => {
//     switch (action) {
//         case 'CREATE':
//             return <ReceiptIcon fontSize="small" />;
//         case 'UPDATE':
//             return <UpdateIcon fontSize="small" />;
//         case 'STATUS_CHANGE':
//             return <CheckCircleIcon fontSize="small" />;
//         case 'PAYMENT_RECEIVED':
//             return <PaymentIcon fontSize="small" />;
//         case 'EMAIL_SENT':
//             return <EmailIcon fontSize="small" />;
//         case 'PRINTED':
//             return <PrintIcon fontSize="small" />;
//         case 'CANCELLED':
//             return <CancelIcon fontSize="small" />;
//         default:
//             return <HistoryIcon fontSize="small" />;
//     }
// };

// const getActionColor = (action: HistoryAction): 'primary' | 'success' | 'info' | 'warning' | 'error' | 'default' | 'secondary' => {
//     switch (action) {
//         case 'CREATE':
//             return 'success';
//         case 'UPDATE':
//             return 'info';
//         case 'STATUS_CHANGE':
//             return 'primary';
//         case 'PAYMENT_RECEIVED':
//             return 'success';
//         case 'EMAIL_SENT':
//             return 'info';
//         case 'PRINTED':
//             return 'secondary';
//         case 'CANCELLED':
//             return 'error';
//         default:
//             return 'success';
//     }
// };

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR'
    }).format(amount);
};

const formatDateTime = (dateString: string) => {
    try {
        return format(new Date(dateString), 'dd MMM yyyy, hh:mm a');
    } catch {
        return dateString;
    }
};

const InvoiceHistoryTimeline: React.FC<InvoiceHistoryTimelineProps> = ({ invoiceId, gstType }) => {
    const [history, setHistory] = useState<InvoiceHistory[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

    const fetchHistory = useCallback(async () => {
        setLoading(true);
        try {
            const res = await request({
                url: `invoice/invoice-history/${invoiceId}`,
                method: 'get',
            });

            if (res.success) {
                setHistory(res.data || []);
            } else {
                setError(res.message || 'Failed to fetch history');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch history');
            console.error('Error fetching invoice history:', err);
        } finally {
            setLoading(false);
        }
    }, [invoiceId]);

    useEffect(() => {
        fetchHistory();
    }, [invoiceId, fetchHistory]);

    const toggleExpand = (id: string) => {
        setExpandedItems(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 2 }}>
                    Loading history...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mt: 2 }}>
                {error}
            </Alert>
        );
    }

    if (history.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'background.default', borderRadius: 1 }}>
                <HistoryIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                    No history available for this invoice
                </Typography>
            </Box>
        );
    }

    return (
        <Card>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <HistoryIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                        Invoice History Timeline
                    </Typography>
                    <Chip
                        label={`${history.length} events`}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                </Box>

                {/* Add horizontal scroll container */}
                <Box sx={{
                     maxHeight: 800, // Set a fixed max height
                     overflowY: 'auto', // Enable vertical scroll
                     overflowX: 'hidden', // Hide horizontal scroll
                     pr: 1, // Add padding right for scrollbar
                     '&::-webkit-scrollbar': {
                         width: 4, // Narrow scrollbar width (reduced from 8 to 4)
                     },
                     '&::-webkit-scrollbar-track': {
                         backgroundColor: 'grey.100',
                         borderRadius: 1,
                         width: 4, // Match track width to thumb
                     },
                     '&::-webkit-scrollbar-thumb': {
                         backgroundColor: 'grey.400',
                         borderRadius: 1,
                         width: 4, // Match thumb width to track
                         '&:hover': {
                             backgroundColor: 'grey.500',
                         },
                     },
                     // For Firefox
                     scrollbarWidth: 'thin', // Makes scrollbar thinner in Firefox
                     scrollbarColor: '#b0b0b0 #f5f5f5',
                }}>
                    <Timeline
                        position="right"
                        sx={{
                            minWidth: 'min-content', // Prevents timeline from shrinking
                            width: '100%',
                        }}
                    >
                        {history.map((item, index) => (
                            <TimelineItem key={item._id}>
                                <TimelineOppositeContent
                                    sx={{
                                        m: 'auto 0',
                                        flex: 0.2,
                                        textAlign: 'right',
                                        minWidth: 120, // Ensure minimum width for date
                                    }}
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {formatDateTime(item.changedAt)}
                                </TimelineOppositeContent>
                                <TimelineSeparator>
                                    {/* <TimelineDot color={getActionColor(item.action)}>
                                        {getActionIcon(item.action)}
                                    </TimelineDot> */}
                                    {index < history.length - 1 && <TimelineConnector />}
                                </TimelineSeparator>
                                <TimelineContent sx={{
                                    py: '12px',
                                    px: 2,
                                    flex: 0.8,
                                    minWidth: 300, // Ensure minimum width for content
                                }}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            bgcolor: 'background.default',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            minWidth: 280, // Ensure paper has minimum width
                                        }}
                                    >
                                        {/* Rest of your content remains the same */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                                                {item.action.replace('_', ' ')}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {item.notes && (
                                                    <Tooltip title="Show Details">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggleExpand(item._id)}
                                                            sx={{ p: 0.5 }}
                                                        >
                                                            {expandedItems[item._id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Chip
                                                    label={item.gstType}
                                                    size="small"
                                                    color={item.gstType === 'GST' ? 'primary' : 'default'}
                                                    variant="outlined"
                                                    sx={{ fontSize: '0.7rem' }}
                                                />
                                            </Box>
                                        </Box>

                                        {/* Show status change */}
                                        {item.action === 'STATUS_CHANGE' && item.previousStatus && item.newStatus && (
                                            <Box sx={{ mb: 1 }}>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Status changed from
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                                    <Chip
                                                        label={item.previousStatus}
                                                        size="small"
                                                        color="default"
                                                        sx={{ fontSize: '0.7rem' }}
                                                    />
                                                    <Typography variant="caption">→</Typography>
                                                    <Chip
                                                        label={item.newStatus}
                                                        size="small"
                                                        color={item.newStatus === 'PAID' ? 'success' : 'primary'}
                                                        sx={{ fontSize: '0.7rem' }}
                                                    />
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Show payment received */}
                                        {item.action === 'PAYMENT_RECEIVED' && item.previousAmount !== undefined && item.newAmount !== undefined && (
                                            <Box sx={{ mb: 1 }}>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Payment received: <strong>{formatCurrency(item.newAmount - item.previousAmount)}</strong>
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Total received: {formatCurrency(item.newAmount)}
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* Show amount change */}
                                        {item.action === 'UPDATE' && item.previousAmount !== undefined && item.newAmount !== undefined && (
                                            <Box sx={{ mb: 1 }}>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Amount changed from {formatCurrency(item.previousAmount)} to {formatCurrency(item.newAmount)}
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* Show notes */}
                                        {item.notes && (
                                            <Typography variant="body2" sx={{ mt: 1, fontSize: '0.85rem' }}>
                                                {item.notes}
                                            </Typography>
                                        )}

                                        {/* Show detailed changes when expanded */}
                                        <Collapse in={expandedItems[item._id]}>
                                            {item.changes && item.changes.length > 0 && (
                                                <Box sx={{ mt: 2, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                                        Detailed Changes:
                                                    </Typography>
                                                    {item.changes.map((change, idx) => (
                                                        <Box key={idx} sx={{ mb: 1, fontSize: '0.8rem' }}>
                                                            <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                                                                {change.field}:
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        p: 0.5,
                                                                        bgcolor: 'error.light',
                                                                        color: 'error.contrastText',
                                                                        borderRadius: 0.5,
                                                                        maxWidth: '200px',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis'
                                                                    }}
                                                                >
                                                                    {change.oldValue !== null && change.oldValue !== undefined
                                                                        ? typeof change.oldValue === 'object'
                                                                            ? JSON.stringify(change.oldValue).substring(0, 50) + (JSON.stringify(change.oldValue).length > 50 ? '...' : '')
                                                                            : String(change.oldValue)
                                                                        : 'null'}
                                                                </Typography>
                                                                <Typography variant="caption">→</Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        p: 0.5,
                                                                        bgcolor: 'success.light',
                                                                        color: 'success.contrastText',
                                                                        borderRadius: 0.5,
                                                                        maxWidth: '200px',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis'
                                                                    }}
                                                                >
                                                                    {change.newValue !== null && change.newValue !== undefined
                                                                        ? typeof change.newValue === 'object'
                                                                            ? JSON.stringify(change.newValue).substring(0, 50) + (JSON.stringify(change.newValue).length > 50 ? '...' : '')
                                                                            : String(change.newValue)
                                                                        : 'null'}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}
                                        </Collapse>

                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                            Changed by: {item.changedBy || 'system'}
                                        </Typography>
                                    </Paper>
                                </TimelineContent>
                            </TimelineItem>
                        ))}
                    </Timeline>
                </Box>
            </CardContent>
        </Card>
    );
};

export default InvoiceHistoryTimeline;